import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { API_URL, LOCAL_API_URL } from './Config';
import './DeployPage.css';

// Helper function to determine if we should use local API
const isLocalMode = () => {
  const isLocalhost = window.location.hostname === 'localhost' && window.location.port === '3000';
  const isLocalAPI = API_URL === 'http://localhost:8000';
  return isLocalhost || isLocalAPI;
};

function DeployPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();

  // Check if running on localhost to conditionally show/hide UI elements
  const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

  const [videoFiles, setVideoFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [project, setProject] = useState(null);
  // Removed old modal states - now using smart queue system

  // New YT generation queue states
  const [ytGenerationProgress, setYtGenerationProgress] = useState({
    step: '', // 'tutorial', 'english', 'translations'
    current: 0,
    total: 0,
    currentLanguage: '',
    isRunning: false
  });
  const [youtubeDetails, setYoutubeDetails] = useState({});
  const [localInputs, setLocalInputs] = useState({}); // Local state for inputs
  const [isPreparingDeployment, setIsPreparingDeployment] = useState(false);
  const [thumbnails, setThumbnails] = useState({});
  const [isPublishing, setIsPublishing] = useState(false);
  const [deploymentResults, setDeploymentResults] = useState(null);
  const [deploymentErrors, setDeploymentErrors] = useState([]);
  const [analyzingAudio, setAnalyzingAudio] = useState({}); // Track which videos are being analyzed
  const [audioAnalysisResults, setAudioAnalysisResults] = useState({});
  const [showAudioAnalysis, setShowAudioAnalysis] = useState({});
  const [audioQualityData, setAudioQualityData] = useState({}); // Saved quality data from database
  const [publishEnabled, setPublishEnabled] = useState({}); // Track which videos are enabled for publishing

  const languageNames = {
    'en': 'English',
    'es': 'Spanish', 
    'fr': 'French',
    'de': 'German',
    'zh': 'Chinese (Simplified)',
    'ja': 'Japanese',
    'ar': 'Arabic',
    'hi': 'Hindi',
    'pt': 'Portuguese',
    'ru': 'Russian',
    'it': 'Italian',
    'ko': 'Korean',
    'nl': 'Dutch',
    'sv': 'Swedish',
    'tr': 'Turkish',
    'ta': 'Tamil',
    'te': 'Telugu',
    'id': 'Indonesian'
  };

  // Initialize local inputs when videos are loaded
  useEffect(() => {
    if (videoFiles.length > 0) {
      const initialLocalInputs = {};
      videoFiles.forEach(video => {
        initialLocalInputs[video.language_code] = {
          title: youtubeDetails[video.language_code]?.title || '',
          description: youtubeDetails[video.language_code]?.description || ''
        };
      });
      setLocalInputs(initialLocalInputs);
    }
  }, [videoFiles, youtubeDetails]);

  useEffect(() => {
    fetchProjectData();
    fetchVideoFiles();
    fetchYouTubeDetails();
  }, [projectId]);

  const fetchProjectData = async () => {
    try {
      const response = await fetch(`${API_URL}/projects/${projectId}`);
      if (response.ok) {
        const projectData = await response.json();
        setProject(projectData);
      }
    } catch (error) {
      console.error('Error fetching project:', error);
    }
  };

  const fetchVideoFiles = async () => {
    try {
      setLoading(true);
      // Use local API when in local mode, live API otherwise
      const currentApiUrl = isLocalMode() ? LOCAL_API_URL : API_URL;
      console.log(`🎬 Deploy Page: Using ${currentApiUrl} for video files`);

      // Add cache-busting parameter to ensure fresh data
      const cacheBuster = new Date().getTime();
      const response = await fetch(`${currentApiUrl}/deploy/videos/${projectId}?_t=${cacheBuster}`, {
        cache: 'no-cache',
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch video files: ${response.status}`);
      }
      
      const data = await response.json();
      setVideoFiles(data.videos || []);
      setAudioQualityData(data.audio_quality_data || {});
    } catch (err) {
      console.error('Error fetching video files:', err);
      setError('Failed to load video files');
    } finally {
      setLoading(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'Unknown';
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const getLanguageDisplayName = (langCode) => {
    return languageNames[langCode] || langCode.toUpperCase();
  };

  // Helper to get the appropriate API URL for the current environment
  const getCurrentApiUrl = () => {
    return isLocalMode() ? LOCAL_API_URL : API_URL;
  };

  const fetchYouTubeDetails = async () => {
    try {
      const currentApiUrl = isLocalMode() ? LOCAL_API_URL : API_URL;
      const response = await fetch(`${currentApiUrl}/deploy/youtube-details/${projectId}`);
      if (response.ok) {
        const data = await response.json();
        // The backend returns data in format: {project_id, details: {en: {...}, hi: {...}}, total_languages}
        if (data.details) {
          setYoutubeDetails(data.details);
          // Initialize local inputs with current values
          const localData = {};
          Object.keys(data.details).forEach(langCode => {
            localData[langCode] = {
              title: data.details[langCode]?.title || '',
              description: data.details[langCode]?.description || ''
            };
          });
          setLocalInputs(localData);
        }
      }
    } catch (error) {
      console.error('Error fetching YouTube details:', error);
    }
  };

  // Smart YT Description Generation with Queue System
  const handleGenerateYTDescriptions = async () => {
    console.log('🚀 Starting smart YouTube description generation...');

    // Get visible video languages from current videoFiles
    const visibleLanguages = videoFiles.map(video => video.language || 'original');
    const uniqueLanguages = [...new Set(visibleLanguages)];

    console.log('📋 Visible languages on deploy page:', uniqueLanguages);

    // Check if there's already a session in progress
    const existingSession = localStorage.getItem('ytGenerationSession');
    if (existingSession) {
      console.log('⏯️ Resuming existing YT generation session...');
      resumeYTGenerationQueue();
      return;
    }

    // Initialize new YT generation session
    const session = {
      projectId: projectId,
      visibleLanguages: uniqueLanguages,
      currentStep: 1, // 1=tutorial, 2=english, 3=translations
      currentLanguageIndex: 0,
      tutorialGenerated: false,
      englishGenerated: false,
      translationsCompleted: [],
      createdAt: new Date().toISOString()
    };

    // Save session to localStorage
    localStorage.setItem('ytGenerationSession', JSON.stringify(session));

    // Start the queue processing
    setYtGenerationProgress({
      step: 'tutorial',
      current: 0,
      total: uniqueLanguages.length + 1, // tutorial/english + translations (english not counted in translations)
      currentLanguage: '',
      isRunning: true
    });

    // Start Step 1: Check/Generate Tutorial
    processYTGenerationQueue();
  };

  // Resume existing YT generation queue
  const resumeYTGenerationQueue = () => {
    const sessionData = localStorage.getItem('ytGenerationSession');
    if (sessionData) {
      const session = JSON.parse(sessionData);
      const nonEnglishLanguages = session.visibleLanguages.filter(lang => lang !== 'original');
      setYtGenerationProgress({
        step: session.currentStep === 1 ? 'tutorial' : session.currentStep === 2 ? 'english' : 'translations',
        current: session.translationsCompleted.length + (session.tutorialGenerated ? 1 : 0) + (session.englishGenerated ? 1 : 0),
        total: nonEnglishLanguages.length + 2,
        currentLanguage: session.currentStep === 3 && session.currentLanguageIndex < session.visibleLanguages.length ? session.visibleLanguages[session.currentLanguageIndex] : '',
        isRunning: true
      });
      processYTGenerationQueue();
    }
  };

  // Process YT Generation Queue
  const processYTGenerationQueue = async () => {
    try {
      const sessionData = localStorage.getItem('ytGenerationSession');
      if (!sessionData) {
        console.error('❌ No YT generation session found');
        return;
      }

      let session = JSON.parse(sessionData);
      console.log('📊 Processing YT generation queue, current step:', session.currentStep);

      if (session.currentStep === 1) {
        // Step 1: Check/Generate Tutorial
        await processTutorialStep(session);
      } else if (session.currentStep === 2) {
        // Step 2: Generate English Content
        await processEnglishStep(session);
      } else if (session.currentStep === 3) {
        // Step 3: Generate Translations
        await processTranslationStep(session);
      } else {
        // All steps completed
        finalizeYTGeneration(session);
      }
    } catch (error) {
      console.error('❌ Error in YT generation queue:', error);
      setYtGenerationProgress(prev => ({ ...prev, isRunning: false }));
      alert(`YT Generation failed: ${error.message}`);
      localStorage.removeItem('ytGenerationSession');
    }
  };

  // Step 1: Check/Generate Tutorial
  const processTutorialStep = async (session) => {
    console.log('📝 FRONTEND: Processing Step 1: Tutorial generation...');

    setYtGenerationProgress(prev => ({
      ...prev,
      step: 'tutorial',
      currentLanguage: 'Checking tutorial...'
    }));

    try {
      // Check if tutorial already exists
      const currentApiUrl = isLocalMode() ? LOCAL_API_URL : API_URL;
      console.log(`🔍 FRONTEND: Checking tutorial status for project ${projectId}`);
      console.log(`🔍 FRONTEND: Tutorial check URL: ${currentApiUrl}/projects/${projectId}/tutorial-status`);

      const checkResponse = await fetch(`${currentApiUrl}/projects/${projectId}/tutorial-status`);
      console.log(`📡 FRONTEND: Tutorial check response: ${checkResponse.status}`);

      if (checkResponse.ok) {
        const tutorialStatus = await checkResponse.json();
        console.log('📄 FRONTEND: Tutorial status:', tutorialStatus);

        if (tutorialStatus.exists) {
          console.log('✅ FRONTEND: Tutorial already exists, skipping generation');
          console.log(`📝 FRONTEND: Tutorial content preview: ${tutorialStatus.content?.substring(0, 200)}...`);
          session.tutorialGenerated = true;
        } else {
          console.log('🔄 FRONTEND: Tutorial not found, generating...');

          // Generate tutorial (same as "Generate Tutorial" button)
          const generateResponse = await fetch(`${currentApiUrl}/projects/${projectId}/generate-tutorial`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' }
          });

          console.log(`📡 FRONTEND: Tutorial generation response: ${generateResponse.status}`);

          if (!generateResponse.ok) {
            const errorText = await generateResponse.text();
            console.error(`❌ FRONTEND: Tutorial generation failed: ${generateResponse.status}`);
            console.error(`❌ FRONTEND: Error details: ${errorText}`);
            throw new Error(`Tutorial generation failed: ${generateResponse.status} - ${errorText}`);
          }

          const generateResult = await generateResponse.json();
          console.log('✅ FRONTEND: Tutorial generated successfully:', generateResult);
          session.tutorialGenerated = true;
        }
      } else {
        const errorText = await checkResponse.text();
        console.error(`❌ FRONTEND: Tutorial check failed: ${checkResponse.status}`);
        console.error(`❌ FRONTEND: Error details: ${errorText}`);
        throw new Error(`Tutorial check failed: ${checkResponse.status} - ${errorText}`);
      }

      // Move to Step 2
      session.currentStep = 2;
      localStorage.setItem('ytGenerationSession', JSON.stringify(session));

      // Continue to next step
      setTimeout(() => processYTGenerationQueue(), 1000);

    } catch (error) {
      console.error('❌ Tutorial step failed:', error);
      throw error;
    }
  };

  // Step 2: Generate English Content
  const processEnglishStep = async (session) => {
    console.log('🇺🇸 Processing Step 2: English content generation...');

    setYtGenerationProgress(prev => ({
      ...prev,
      step: 'english',
      current: 1,
      currentLanguage: 'Generating English content...'
    }));

    try {
      const currentApiUrl = isLocalMode() ? LOCAL_API_URL : API_URL;

      console.log(`🔍 FRONTEND: Starting English generation for project ${projectId}`);
      console.log(`🔍 FRONTEND: API URL: ${currentApiUrl}/yt-generation/generate-english/${projectId}`);

      // Generate SEO-optimized English YouTube content using tutorial
      const response = await fetch(`${currentApiUrl}/yt-generation/generate-english/${projectId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });

      console.log(`📡 FRONTEND: Response status: ${response.status}`);

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ FRONTEND: English generation failed: ${response.status}`);
        console.error(`❌ FRONTEND: Error details: ${errorText}`);
        throw new Error(`English generation failed: ${response.status} - ${errorText}`);
      }

      const result = await response.json();
      console.log('✅ FRONTEND: English content generated:', result);
      console.log(`📝 FRONTEND: Generated title: "${result.title}"`);
      console.log(`📝 FRONTEND: Description length: ${result.description?.length || 0} chars`);

      // Save English content to YouTube details
      setYoutubeDetails(prev => ({
        ...prev,
        'original': {
          title: result.title,
          description: result.description,
          language_code: 'original'
        }
      }));

      session.englishGenerated = true;
      session.currentStep = 3;
      session.currentLanguageIndex = 0;
      localStorage.setItem('ytGenerationSession', JSON.stringify(session));

      // Continue to Step 3
      setTimeout(() => processYTGenerationQueue(), 1000);

    } catch (error) {
      console.error('❌ English generation step failed:', error);
      throw error;
    }
  };

  // Step 3: Generate Translations
  const processTranslationStep = async (session) => {
    console.log('🌐 Processing Step 3: Translations...');

    const nonEnglishLanguages = session.visibleLanguages.filter(lang => lang !== 'original');

    if (session.currentLanguageIndex >= nonEnglishLanguages.length) {
      // All translations completed
      session.currentStep = 4;
      localStorage.setItem('ytGenerationSession', JSON.stringify(session));
      setTimeout(() => processYTGenerationQueue(), 500);
      return;
    }

    const currentLanguage = nonEnglishLanguages[session.currentLanguageIndex];

    // Skip if already completed
    if (session.translationsCompleted.includes(currentLanguage)) {
      session.currentLanguageIndex++;
      localStorage.setItem('ytGenerationSession', JSON.stringify(session));
      setTimeout(() => processYTGenerationQueue(), 500);
      return;
    }

    console.log(`🔄 Translating for language: ${currentLanguage}`);

    setYtGenerationProgress(prev => ({
      ...prev,
      step: 'translations',
      current: session.translationsCompleted.length + 2, // +2 for tutorial and english
      currentLanguage: currentLanguage
    }));

    try {
      const currentApiUrl = isLocalMode() ? LOCAL_API_URL : API_URL;

      // Translate YouTube content
      const response = await fetch(`${currentApiUrl}/yt-generation/translate/${projectId}/${currentLanguage}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });

      if (!response.ok) {
        throw new Error(`Translation failed for ${currentLanguage}: ${response.status}`);
      }

      const result = await response.json();
      console.log(`✅ Translation completed for ${currentLanguage}:`, result);

      // Save translated content
      setYoutubeDetails(prev => ({
        ...prev,
        [currentLanguage]: {
          title: result.title,
          description: result.description,
          language_code: currentLanguage
        }
      }));

      // Mark as completed and move to next language
      session.translationsCompleted.push(currentLanguage);
      session.currentLanguageIndex++;
      localStorage.setItem('ytGenerationSession', JSON.stringify(session));

      // Continue with next language
      setTimeout(() => processYTGenerationQueue(), 1000);

    } catch (error) {
      console.error(`❌ Translation step failed for ${currentLanguage}:`, error);

      // Mark as failed but continue with next language
      session.currentLanguageIndex++;
      localStorage.setItem('ytGenerationSession', JSON.stringify(session));
      setTimeout(() => processYTGenerationQueue(), 1000);
    }
  };

  // Finalize YT Generation
  const finalizeYTGeneration = (session) => {
    console.log('🎉 YT Generation completed successfully!');

    setYtGenerationProgress({
      step: 'completed',
      current: session.translationsCompleted.length + 2,
      total: session.translationsCompleted.length + 2,
      currentLanguage: 'All done!',
      isRunning: false
    });

    // Clean up
    localStorage.removeItem('ytGenerationSession');

    // Show completion message
    setTimeout(() => {
      alert(`🎉 YouTube descriptions generated successfully!\n\n✅ Tutorial: Generated\n✅ English: Generated\n✅ Translations: ${session.translationsCompleted.length} languages\n\nYou can now review and edit the content before publishing.`);

      // Reset progress after showing message
      setYtGenerationProgress({
        step: '',
        current: 0,
        total: 0,
        currentLanguage: '',
        isRunning: false
      });
    }, 2000);
  };

  const handleInputChange = (languageCode, field, value) => {
    // Only update local state, no API call
    setLocalInputs(prev => ({
      ...prev,
      [languageCode]: {
        ...prev[languageCode],
        [field]: value
      }
    }));
  };

  const handleSaveYouTubeDetails = async (languageCode) => {
    try {
      console.log(`💾 Saving YouTube details for ${languageCode}`);

      const localData = localInputs[languageCode] || {};
      const title = localData.title || '';
      const description = localData.description || '';

      const updatedData = { title, description };

      console.log('💾 Sending data to API:', updatedData);

      const currentApiUrl = isLocalMode() ? LOCAL_API_URL : API_URL;
      const response = await fetch(`${currentApiUrl}/deploy/youtube-details/${projectId}/${languageCode}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updatedData)
      });

      if (response.ok) {
        const result = await response.json();
        console.log(`✅ Saved YouTube details for ${languageCode}`);

        // Update the main state with saved data
        setYoutubeDetails(prev => ({
          ...prev,
          [languageCode]: result.detail
        }));

        // Show success feedback briefly
        alert('YouTube details saved successfully!');
      } else {
        console.error(`❌ Failed to save YouTube details for ${languageCode}:`, response.status);
        const errorText = await response.text();
        console.error('Error details:', errorText);
        alert('Failed to save YouTube details. Please check console for details.');
      }
    } catch (error) {
      console.error(`❌ Error saving YouTube details for ${languageCode}:`, error);
      alert('Error saving YouTube details. Please check console for details.');
    }
  };

  const handleRemoveYouTubeDetails = async (languageCode) => {
    if (!window.confirm(`Are you sure you want to remove the YouTube title and description for ${getLanguageDisplayName(languageCode)}?`)) {
      return;
    }

    try {
      console.log(`🗑️ Removing YouTube details for ${languageCode}`);

      const currentApiUrl = isLocalMode() ? LOCAL_API_URL : API_URL;
      const response = await fetch(`${currentApiUrl}/yt-generation/remove/${projectId}/${languageCode}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (response.ok) {
        const result = await response.json();
        console.log(`✅ Removed YouTube details for ${languageCode}:`, result);

        // Remove from main state
        setYoutubeDetails(prev => {
          const newState = { ...prev };
          delete newState[languageCode];
          return newState;
        });

        // Clear local inputs
        setLocalInputs(prev => ({
          ...prev,
          [languageCode]: {
            title: '',
            description: ''
          }
        }));

        // Show success feedback
        if (result.success) {
          alert(`YouTube details removed for ${getLanguageDisplayName(languageCode)}`);
        } else {
          alert(result.message || `No YouTube content found for ${getLanguageDisplayName(languageCode)}`);
        }
      } else {
        console.error(`❌ Failed to remove YouTube details for ${languageCode}:`, response.status);
        const errorText = await response.text();
        console.error('Error details:', errorText);
        alert('Failed to remove YouTube details. Please check console for details.');
      }
    } catch (error) {
      console.error(`❌ Error removing YouTube details for ${languageCode}:`, error);
      alert('Error removing YouTube details. Please check console for details.');
    }
  };

  const handlePrepareDeployment = () => {
    setIsPreparingDeployment(true);
  };

  const handleThumbnailUpload = async (languageCode, file) => {
    if (!file) return;

    const formData = new FormData();
    formData.append('thumbnail', file);
    formData.append('project_id', projectId);
    formData.append('language_code', languageCode);

    try {
      const currentApiUrl = isLocalMode() ? LOCAL_API_URL : API_URL;
      const response = await fetch(`${currentApiUrl}/deploy/upload-thumbnail`, {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        setThumbnails(prev => ({
          ...prev,
          [languageCode]: {
            file: file,
            url: data.thumbnail_url,
            uploaded: true
          }
        }));
      } else {
        console.error('Failed to upload thumbnail');
      }
    } catch (error) {
      console.error('Error uploading thumbnail:', error);
    }
  };

  const handlePublish = async () => {
    setIsPublishing(true);
    setDeploymentResults(null);
    setDeploymentErrors([]);
    
    // Get list of enabled language codes
    const enabledLanguages = videoFiles
      .filter(video => publishEnabled[video.language_code])
      .map(video => video.language_code);
    
    try {
      const currentApiUrl = isLocalMode() ? LOCAL_API_URL : API_URL;
      const response = await fetch(`${currentApiUrl}/deploy/publish-videos/${projectId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          languages: enabledLanguages
        })
      });

      if (response.ok) {
        const result = await response.json();
        console.log('Publishing result:', result);
        
        // Set deployment results
        setDeploymentResults(result);
        
        // Extract and display errors for failed videos
        const errors = result.results
          .filter(r => r.status === 'failed')
          .map(r => ({
            language_code: r.language_code,
            language_name: getLanguageDisplayName(r.language_code),
            error: r.error || 'Unknown error occurred'
          }));
        
        setDeploymentErrors(errors);
        
        // If all videos published successfully, reset deployment state
        if (result.failed_videos === 0) {
          setIsPreparingDeployment(false);
          setThumbnails({});
        }
      } else {
        const errorText = await response.text();
        setDeploymentErrors([{
          language_code: 'general',
          language_name: 'General',
          error: `Failed to publish videos: ${errorText}`
        }]);
      }
    } catch (error) {
      console.error('Error publishing videos:', error);
      setDeploymentErrors([{
        language_code: 'general',
        language_name: 'General',
        error: `Network error: ${error.message}`
      }]);
    } finally {
      setIsPublishing(false);
    }
  };

  const areAllThumbnailsUploaded = () => {
    const enabledVideos = videoFiles.filter(video => publishEnabled[video.language_code]);
    return enabledVideos.length > 0 && enabledVideos.every(video => 
      thumbnails[video.language_code] && thumbnails[video.language_code].uploaded
    );
  };

  const getEnabledVideosCount = () => {
    return videoFiles.filter(video => publishEnabled[video.language_code]).length;
  };

  const handleIndividualAudioAnalysis = async (languageCode) => {
    setAnalyzingAudio(prev => ({ ...prev, [languageCode]: true }));
    
    try {
      const currentApiUrl = isLocalMode() ? LOCAL_API_URL : API_URL;
      const response = await fetch(`${currentApiUrl}/deploy/analyze-audio/${projectId}/${languageCode}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (response.ok) {
        const data = await response.json();
        setAudioAnalysisResults(prev => ({
          ...prev,
          [languageCode]: data.analysis_result
        }));
        
        // Show the analysis result for this video
        setShowAudioAnalysis(prev => ({
          ...prev,
          [languageCode]: true
        }));
      } else {
        console.error(`Failed to analyze audio for ${languageCode}`);
        alert(`Failed to analyze audio for ${getLanguageDisplayName(languageCode)}`);
      }
    } catch (error) {
      console.error(`Error analyzing audio for ${languageCode}:`, error);
      alert(`Error analyzing audio for ${getLanguageDisplayName(languageCode)}: ${error.message}`);
    } finally {
      setAnalyzingAudio(prev => ({ ...prev, [languageCode]: false }));
    }
  };

  const toggleAudioAnalysis = (languageCode) => {
    setShowAudioAnalysis(prev => ({
      ...prev,
      [languageCode]: !prev[languageCode]
    }));
  };

  const handleShowQualityFeedback = (languageCode) => {
    const qualityData = audioQualityData[languageCode];
    if (!qualityData) return;

    // Create a fake analysis result from the saved data for display
    const feedbackResult = {
      overall_quality: qualityData.quality,
      summary: qualityData.remarks ? qualityData.remarks.split(' | ')[0]?.replace('Summary: ', '') : `Audio quality is ${qualityData.quality}`,
      issues: [],
      recommendations: []
    };

    // Parse remarks if they exist
    if (qualityData.remarks) {
      const parts = qualityData.remarks.split(' | ');
      
      // Parse issues
      const issuesPart = parts.find(p => p.startsWith('Issues: '));
      if (issuesPart) {
        const issuesText = issuesPart.replace('Issues: ', '');
        const issuesList = issuesText.split('; ').map(issue => {
          const parts = issue.split(' at ');
          if (parts.length === 2) {
            const [type, rest] = parts;
            const timestampEnd = rest.indexOf(':');
            const timestamp = timestampEnd > -1 ? rest.substring(0, timestampEnd) : 'unknown';
            const description = timestampEnd > -1 ? rest.substring(timestampEnd + 2) : rest;
            return { type, timestamp, description };
          }
          return { type: 'Issue', timestamp: 'unknown', description: issue };
        });
        feedbackResult.issues = issuesList;
      }

      // Parse recommendations
      const recsPart = parts.find(p => p.startsWith('Recommendations: '));
      if (recsPart) {
        const recsText = recsPart.replace('Recommendations: ', '');
        feedbackResult.recommendations = recsText.split('; ');
      }
    }

    // Set the result and show the analysis
    setAudioAnalysisResults(prev => ({
      ...prev,
      [languageCode]: feedbackResult
    }));

    setShowAudioAnalysis(prev => ({
      ...prev,
      [languageCode]: true
    }));
  };

  const togglePublishEnabled = (languageCode) => {
    setPublishEnabled(prev => ({
      ...prev,
      [languageCode]: !prev[languageCode]
    }));
  };



  if (loading) {
    return (
      <div className="deploy-page">
        <div className="deploy-header">
          <button onClick={() => navigate(`/project/${projectId}`)} className="back-button">
            ← Back to Project
          </button>
          <h1>Deploy</h1>
        </div>
        <div className="loading">Loading video files...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="deploy-page">
        <div className="deploy-header">
          <button onClick={() => navigate(`/project/${projectId}`)} className="back-button">
            ← Back to Project
          </button>
          <h1>Deploy</h1>
        </div>
        <div className="error-message">
          <p>{error}</p>
          <button onClick={fetchVideoFiles} className="retry-button">Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="deploy-page">
      <div className="deploy-header">
        <button onClick={() => navigate(`/project/${projectId}`)} className="back-button">
          ← Back to Project
        </button>
        <div className="header-info">
          <h1>Deploy</h1>
          {project && <p className="project-title">{project.title}</p>}
        </div>
        <div className="header-buttons">
          
          <button
            onClick={fetchVideoFiles}
            className="refresh-videos-button"
            disabled={loading}
          >
            {loading ? 'Refreshing...' : '🔄 Refresh Videos'}
          </button>

          {/* Quick Preview button - always visible */}
          <button
            onClick={() => window.location.href = `/project/${projectId}/quick-preview`}
            className="quick-preview-button"
            title="Preview your project videos"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-play-circle-fill" viewBox="0 0 16 16">
              <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zM6.79 5.093A.5.5 0 0 0 6 5.5v5a.5.5 0 0 0 .79.407l3.5-2.5a.5.5 0 0 0 0-.814l-3.5-2.5z"/>
            </svg>
            Quick Preview
          </button>

          {!isLocalhost && (
            <button
              onClick={handleGenerateYTDescriptions}
              className="generate-yt-button"
              disabled={ytGenerationProgress.isRunning}
            >
              {ytGenerationProgress.isRunning ?
                `${ytGenerationProgress.step === 'tutorial' ? '📝 Checking Tutorial...' :
                  ytGenerationProgress.step === 'english' ? '🇺🇸 Generating English...' :
                  ytGenerationProgress.step === 'translations' ? `🌐 Translating (${ytGenerationProgress.current}/${ytGenerationProgress.total})...` :
                  'Processing...'}`
                : 'Generate YT Description'}
            </button>
          )}
          
          {!isPreparingDeployment ? (
            !isLocalhost && (
              <button
                onClick={handlePrepareDeployment}
                className="prepare-deploy-button"
                disabled={videoFiles.length === 0 || Object.keys(youtubeDetails).length === 0}
              >
                Prepare for Deployment
              </button>
            )
          ) : (
            <button 
              onClick={handlePublish}
              className="publish-button"
              disabled={!areAllThumbnailsUploaded() || isPublishing || getEnabledVideosCount() === 0}
            >
              {isPublishing ? 'Publishing...' : `Publish ${getEnabledVideosCount()} Video${getEnabledVideosCount() !== 1 ? 's' : ''}`}
            </button>
          )}
        </div>
      </div>

      {/* Deployment Results Section */}
      {deploymentResults && (
        <div className="deployment-results">
          <div className="results-header">
            <h3>Deployment Results</h3>
            <div className="results-summary">
              <span className="success-count">✓ {deploymentResults.published_videos} Published</span>
              {deploymentResults.failed_videos > 0 && (
                <span className="error-count">✗ {deploymentResults.failed_videos} Failed</span>
              )}
            </div>
          </div>
          
          {deploymentResults.failed_videos === 0 ? (
            <div className="success-message">
              🎉 All videos published successfully to YouTube!
            </div>
          ) : (
            <div className="results-details">
              {deploymentResults.results.map((result, index) => (
                <div 
                  key={index} 
                  className={`result-item ${result.status}`}
                >
                  <div className="result-language">
                    {getLanguageDisplayName(result.language_code)} ({result.language_code})
                  </div>
                  <div className="result-status">
                    {result.status === 'success' ? (
                      <span className="success">✓ Published</span>
                    ) : (
                      <span className="failed">✗ Failed</span>
                    )}
                  </div>
                  {result.video_id && (
                    <div className="video-info">
                      <div className="video-id">Video ID: {result.video_id}</div>
                      {result.url && (
                        <a href={result.url} target="_blank" rel="noopener noreferrer" className="video-link">
                          View on YouTube →
                        </a>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Error Display Section */}
      {deploymentErrors.length > 0 && (
        <div className="deployment-errors">
          <div className="error-header">
            <h3>⚠️ Deployment Errors</h3>
          </div>
          <div className="error-list">
            {deploymentErrors.map((error, index) => (
              <div key={index} className="error-item">
                <div className="error-language">
                  {error.language_name} ({error.language_code})
                </div>
                <div className="error-message">
                  {error.error}
                </div>
              </div>
            ))}
          </div>
          <div className="error-actions">
            <button 
              className="dismiss-errors-btn"
              onClick={() => setDeploymentErrors([])}
            >
              Dismiss Errors
            </button>
          </div>
        </div>
      )}


      <div className="deploy-content">
        {videoFiles.length === 0 ? (
          <div className="no-videos">
            <div className="no-videos-icon">🎬</div>
            <h3>No Generated Videos Found</h3>
            <p>No final videos are available for deployment yet.</p>
            <p>Generate videos first from your project timeline.</p>
          </div>
        ) : (
          <div className="videos-list">
            {videoFiles.map((video) => (
              <div key={video.language_code} className="video-row">
                <div className="video-preview-section">
                  <div className="language-header">
                    <h3>{getLanguageDisplayName(video.language_code)}</h3>
                    <span className="language-code">({video.language_code})</span>
                  </div>
                  
                  <div className="video-player-container">
                    <video
                      className="video-player"
                      controls
                      preload="metadata"
                      controlsList="nodownload"
                      key={video.language_code}
                    >
                      <source src={`${getCurrentApiUrl()}/${video.video_path}?_t=${new Date().getTime()}`} type="video/mp4" />
                      Your browser does not support the video tag.
                    </video>
                  </div>

                  <div className="video-metadata-compact">
                    <span className="metadata-item">
                      📁 {formatFileSize(video.file_size)}
                    </span>
                    <span className="metadata-item">
                      📅 {formatDateTime(video.created_at)}
                    </span>
                    <span className="metadata-item">
                      {video.has_audio ? '🔊' : '🔇'} {video.has_audio ? 'Audio' : 'No Audio'}
                    </span>
                  </div>

                  <div className="video-actions">
                    <div className="audio-quality-section">
                      {!isLocalhost && (
                        <button
                          onClick={() => handleIndividualAudioAnalysis(video.language_code)}
                          className="analyze-individual-audio-button"
                          disabled={analyzingAudio[video.language_code] || !video.has_audio}
                        >
                          {analyzingAudio[video.language_code] ? (
                            <>
                              <span className="loading-spinner">🔄</span> Analyzing...
                            </>
                          ) : (
                            '🎵 Analyze Audio Quality'
                          )}
                        </button>
                      )}
                      
                      {audioQualityData[video.language_code] && (
                        <div className="audio-quality-status">
                          <button
                            className={`quality-indicator quality-${audioQualityData[video.language_code].quality}`}
                            onClick={() => handleShowQualityFeedback(video.language_code)}
                            title="Click to view detailed feedback"
                          >
                            {audioQualityData[video.language_code].quality?.toUpperCase() || 'UNKNOWN'}
                          </button>
                          <span className="quality-date">
                            {new Date(audioQualityData[video.language_code].analyzed_at).toLocaleDateString()}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="video-content-section">
                  {/* Audio Analysis Overlay */}
                  {showAudioAnalysis[video.language_code] && audioAnalysisResults[video.language_code] && (
                    <div className="audio-analysis-overlay">
                      <div className="audio-analysis-header">
                        <h4>🎵 Audio Quality Analysis</h4>
                        <button 
                          className="close-analysis-btn"
                          onClick={() => toggleAudioAnalysis(video.language_code)}
                          title="Close analysis and show YouTube details"
                        >
                          ×
                        </button>
                      </div>
                      
                      <div className="audio-analysis-content">
                        <div className="overall-status">
                          <span className={`status-indicator ${audioAnalysisResults[video.language_code].overall_quality}`}>
                            {audioAnalysisResults[video.language_code].overall_quality === 'good' ? '✅' : 
                             audioAnalysisResults[video.language_code].overall_quality === 'fair' ? '⚠️' : '❌'} 
                            {audioAnalysisResults[video.language_code].overall_quality.toUpperCase()}
                          </span>
                        </div>
                        
                        {audioAnalysisResults[video.language_code].summary && (
                          <div className="analysis-summary">
                            <strong>Summary:</strong>
                            <p>{audioAnalysisResults[video.language_code].summary}</p>
                          </div>
                        )}
                        
                        {audioAnalysisResults[video.language_code].issues && audioAnalysisResults[video.language_code].issues.length > 0 && (
                          <div className="audio-issues">
                            <strong>Issues Found ({audioAnalysisResults[video.language_code].issues.length}):</strong>
                            <ul>
                              {audioAnalysisResults[video.language_code].issues.map((issue, idx) => (
                                <li key={idx} className="issue-item">
                                  <div className="issue-header">
                                    <div className="issue-type-badge">{issue.type}</div>
                                    <div className="issue-timestamp-precise">
                                      📍 {issue.timestamp}
                                      {issue.start_seconds && issue.end_seconds && (
                                        <span className="timestamp-seconds">
                                          ({Math.round(issue.start_seconds)}s - {Math.round(issue.end_seconds)}s)
                                        </span>
                                      )}
                                      {issue.timeline_item_id && (
                                        <span className="timeline-item-info">
                                          Item #{issue.timeline_item_id}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                  <div className="issue-description">
                                    {issue.description.includes('CLIP #') ? (
                                      <span>
                                        <strong style={{color: '#dc3545', fontSize: '14px'}}>
                                          {issue.description.match(/CLIP #\d+/)?.[0]}:
                                        </strong>
                                        {' ' + issue.description.replace(/CLIP #\d+:\s?/, '')}
                                      </span>
                                    ) : (
                                      issue.description
                                    )}
                                  </div>
                                </li>
                              ))}
                            </ul>

                            {(audioAnalysisResults[video.language_code].analysis_method === 'precise_segmentation' ||
                              audioAnalysisResults[video.language_code].analysis_method === 'timeline_segments') && (
                              <div className="analysis-info">
                                <small>
                                  {audioAnalysisResults[video.language_code].analysis_method === 'timeline_segments' ? (
                                    <>
                                      🎯 Analyzed {audioAnalysisResults[video.language_code].segments_analyzed} timeline items
                                      ({audioAnalysisResults[video.language_code].problematic_segments} with issues)
                                      {audioAnalysisResults[video.language_code].quality_breakdown && (
                                        <div className="quality-breakdown">
                                          Quality: ✅{audioAnalysisResults[video.language_code].quality_breakdown.good} Good,
                                          ⚠️{audioAnalysisResults[video.language_code].quality_breakdown.fair} Fair,
                                          ❌{audioAnalysisResults[video.language_code].quality_breakdown.poor} Poor
                                          {audioAnalysisResults[video.language_code].quality_breakdown.error > 0 && (
                                            <>, 🔥{audioAnalysisResults[video.language_code].quality_breakdown.error} Error</>
                                          )}
                                        </div>
                                      )}
                                    </>
                                  ) : (
                                    <>
                                      📊 Analyzed {audioAnalysisResults[video.language_code].segments_analyzed} segments
                                      ({audioAnalysisResults[video.language_code].problematic_segments} with issues)
                                    </>
                                  )}
                                </small>
                              </div>
                            )}
                          </div>
                        )}

                        {/* New section for comprehensive AI analysis */}
                        {audioAnalysisResults[video.language_code].raw_analysis && (
                          <div className="comprehensive-analysis">
                            <strong>🤖 AI Quality Assessment:</strong>
                            <div className="analysis-details">
                              <div className="analysis-info-badge">
                                📊 Model: {audioAnalysisResults[video.language_code].model_used || 'Gemini 2.5 Flash Lite'}
                                {audioAnalysisResults[video.language_code].target_language && (
                                  <> | 🌐 Expected: {audioAnalysisResults[video.language_code].target_language}</>
                                )}
                              </div>
                              <div className="raw-analysis-content">
                                {audioAnalysisResults[video.language_code].raw_analysis.split('\n').map((line, idx) => (
                                  <p key={idx} className={line.includes('**') ? 'analysis-header' : 'analysis-line'}>
                                    {line}
                                  </p>
                                ))}
                              </div>
                            </div>
                          </div>
                        )}

                        {audioAnalysisResults[video.language_code].recommendations && audioAnalysisResults[video.language_code].recommendations.length > 0 && (
                          <div className="audio-recommendations">
                            <strong>Recommendations:</strong>
                            <ul>
                              {audioAnalysisResults[video.language_code].recommendations.map((rec, idx) => (
                                <li key={idx}>{rec}</li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {audioAnalysisResults[video.language_code].detailed_analysis && audioAnalysisResults[video.language_code].detailed_analysis.length > 0 && (
                          <div className="detailed-analysis">
                            <strong>📋 Clip-by-Clip Analysis:</strong>
                            <div className="detailed-analysis-grid">
                              {audioAnalysisResults[video.language_code].detailed_analysis
                                .filter(clip => clip.quality === 'fair' || clip.quality === 'poor' || clip.quality === 'error')
                                .slice(0, 10) // Show first 10 problematic clips
                                .map((clip, idx) => (
                                <div key={idx} className="clip-analysis-item">
                                  <div className="clip-analysis-header">
                                    <span className="clip-id">Item #{clip.timeline_item_id}</span>
                                    <span className={`clip-quality ${clip.quality}`}>
                                      {clip.quality === 'good' && '✅'}
                                      {clip.quality === 'fair' && '⚠️'}
                                      {clip.quality === 'poor' && '❌'}
                                      {clip.quality === 'error' && '🔥'}
                                      {clip.quality.toUpperCase()}
                                    </span>
                                  </div>
                                  <div className="clip-details">
                                    <div className="narration-preview">{clip.narration_preview}</div>
                                    {clip.has_issues && (
                                      <div className="clip-issue">{clip.issue_description}</div>
                                    )}
                                    <div className="clip-meta">
                                      Confidence: {clip.confidence} | Type: {clip.type}
                                      {!clip.matches_expected_text && <span className="text-mismatch"> | ⚠️ Text mismatch</span>}
                                    </div>
                                  </div>
                                </div>
                              ))}
                              {audioAnalysisResults[video.language_code].detailed_analysis.filter(clip => clip.quality === 'fair' || clip.quality === 'poor' || clip.quality === 'error').length > 10 && (
                                <div className="more-clips-notice">
                                  ... and {audioAnalysisResults[video.language_code].detailed_analysis.filter(clip => clip.quality === 'fair' || clip.quality === 'poor' || clip.quality === 'error').length - 10} more problematic clips
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {(!showAudioAnalysis[video.language_code] || !audioAnalysisResults[video.language_code]) && (
                    <div className="youtube-details-container">
                      <div className="youtube-content">
                        <div className="youtube-content-header">
                          <h4>YouTube Details</h4>

                          {/* Publish Toggle - shown only when preparing deployment */}
                          {isPreparingDeployment && (
                            <div className="publish-toggle-container">
                              <label className="publish-toggle">
                                <input
                                  type="checkbox"
                                  checked={publishEnabled[video.language_code] || false}
                                  onChange={() => togglePublishEnabled(video.language_code)}
                                  className="toggle-checkbox"
                                />
                                <span className="toggle-slider"></span>
                                <span className="toggle-label">Publish</span>
                              </label>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="video-details-section">
                      {/* Thumbnail Upload Field - shown only when preparing deployment and publish is enabled */}
                      {isPreparingDeployment && publishEnabled[video.language_code] && (
                        <div className="thumbnail-upload">
                          <label>Thumbnail</label>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleThumbnailUpload(video.language_code, e.target.files[0])}
                            className="thumbnail-input"
                          />
                          {thumbnails[video.language_code] && (
                            <div className="thumbnail-preview">
                              <img
                                src={URL.createObjectURL(thumbnails[video.language_code].file)}
                                alt="Thumbnail preview"
                                className="thumbnail-image"
                              />
                              <span className="thumbnail-status">✓ Uploaded</span>
                            </div>
                          )}
                        </div>
                      )}

                      {/* YouTube Details Form */}
                      <div className="youtube-form">
                      {console.log(`🔍 Rendering YouTube form for ${video.language_code}:`, youtubeDetails[video.language_code])}
                      <div className="form-group">
                        <label>Title</label>
                        <input
                            type="text"
                            value={localInputs[video.language_code]?.title || ''}
                            onChange={(e) => {
                              handleInputChange(video.language_code, 'title', e.target.value);
                            }}
                            placeholder="Click here to enter YouTube title..."
                            className="youtube-input"
                            autoComplete="off"
                          />
                        </div>
                        <div className="form-group">
                          <label>Description</label>
                          <textarea
                            value={localInputs[video.language_code]?.description || ''}
                            onChange={(e) => {
                              handleInputChange(video.language_code, 'description', e.target.value);
                            }}
                            placeholder="Click here to enter YouTube description..."
                            rows={6}
                            className="youtube-textarea"
                            autoComplete="off"
                          />
                        </div>

                        {/* Save and Remove Buttons */}
                        <div className="form-group">
                          <div className="youtube-actions">
                            <button
                              onClick={() => handleSaveYouTubeDetails(video.language_code)}
                              className="save-youtube-btn"
                              type="button"
                            >
                              💾 Save YouTube Details
                            </button>

                            {(localInputs[video.language_code]?.title || localInputs[video.language_code]?.description || youtubeDetails[video.language_code]) && (
                              <button
                                onClick={() => handleRemoveYouTubeDetails(video.language_code)}
                                className="remove-youtube-btn"
                                type="button"
                                title={`Remove YouTube title and description for ${getLanguageDisplayName(video.language_code)}`}
                              >
                                🗑️ Remove
                              </button>
                            )}
                          </div>
                        </div>

                        {youtubeDetails[video.language_code]?.video_url && (
                          <div className="form-group">
                            <label>Published Video</label>
                            <div className="published-video-info">
                              <a
                                href={youtubeDetails[video.language_code].video_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="published-video-link"
                              >
                                🎥 View on YouTube →
                              </a>
                              <span className="published-status">✅ Published</span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="deploy-footer">
        <div className="stats">
          <span>{videoFiles.length} video{videoFiles.length !== 1 ? 's' : ''} available for deployment</span>
        </div>
      </div>

      {/* Progress indicator for YT generation */}
      {ytGenerationProgress.isRunning && (
        <div className="generation-progress-overlay">
          <div className="generation-progress-modal">
            <div className="progress-header">
              <h3>🎬 Generating YouTube Descriptions</h3>
            </div>
            <div className="progress-content">
              <div className="progress-step">
                <strong>Current Step:</strong> {
                  ytGenerationProgress.step === 'tutorial' ? '📝 Checking/Generating Tutorial' :
                  ytGenerationProgress.step === 'english' ? '🇺🇸 Creating SEO-Optimized English Content' :
                  ytGenerationProgress.step === 'translations' ? '🌐 Generating Translations' :
                  '✅ Finalizing'
                }
              </div>

              {ytGenerationProgress.currentLanguage && (
                <div className="progress-language">
                  <strong>Processing:</strong> {ytGenerationProgress.currentLanguage}
                </div>
              )}

              <div className="progress-bar">
                <div className="progress-bar-fill" style={{
                  width: `${(ytGenerationProgress.current / ytGenerationProgress.total) * 100}%`
                }}></div>
              </div>

              <div className="progress-text">
                {ytGenerationProgress.current}/{ytGenerationProgress.total} completed
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DeployPage;